import React from 'react';
import StandalonePage from './StandalonePage';
import MessageCard from './MessageCard';
import LabeledValueBox from './LabeledValueBox';
import FigmaButton from './FigmaButton';
import ButtonColorsEnum from '../Enums/ButtonColorsEnum';
import ButtonTypeEnum from '../Enums/ButtonTypeEnum';
import IconPositionEnum from '../Enums/IconPositionEnum';
import SignInBlockedReasonEnum from '../Enums/SignInBlockedReasonEnum';

// Landing page for an Azure account that authenticated against AAD but cannot be resolved to a
// Bravo user. It replaces the old retry loop (bounce back to the Microsoft account picker every
// 25s), which gave the user no way to understand or leave the state. See docs/USER_PROVISIONING.md.

// Deep link to the reactivation request type itself (portal 6 → group 51 → request 197), so the
// user lands on the form rather than having to find it. The portal/group/request ids are owned by
// IT: if they re-organise the portal this link dead-ends on the one action the page offers, so it
// is worth re-checking whenever the request type is touched.
const REACTIVATION_REQUEST_URL =
  'https://bareinternational.atlassian.net/servicedesk/customer/portal/6/group/51/create/197';

// Portal home, for the cases where a reactivation request would be the wrong ticket to raise.
const SERVICE_DESK_HOME_URL = 'https://bareinternational.atlassian.net/servicedesk/customer/portals';

// The window is Office.GlobalUserDeactivationDaysLimit on the General office (30 everywhere
// today), read by DeactivateInactiveUsersOperation. It is configurable, so this wording has
// to be revisited if IT ever changes it — noted in docs/USER_PROVISIONING.md.
const INACTIVITY_DAYS = 30;

// One entry per SignInBlockedReasonEnum value. The two differ in what the user should do about
// it, which is the whole point of telling them apart: an inactive account is reactivated by a
// ticket the user raises, while a token with no identity claim is an auth configuration problem
// that no amount of reactivating will fix.
const REASONS = {
  [SignInBlockedReasonEnum.NoActiveUser]: {
    body: `Please note: for security reasons, accounts that haven’t been used to log in for more than ${INACTIVITY_DAYS} days are automatically deactivated. If this applies to you, please submit a reactivation ticket with the Service Desk to restore access.`,
    actionLabel: 'Open the Service Desk',
    actionUrl: REACTIVATION_REQUEST_URL,
  },
  [SignInBlockedReasonEnum.NoIdentityClaim]: {
    body: 'Your Microsoft sign-in completed, but it did not include the account details Bravo needs to identify you. This is a configuration problem on our side rather than something you can fix — please contact the Service Desk so IT can look into it.',
    actionLabel: 'Contact the Service Desk',
    actionUrl: SERVICE_DESK_HOME_URL,
  },
};

export default function SignInUnavailable({
  reason = SignInBlockedReasonEnum.NoActiveUser,
  accountName = '',
  onTryDifferentAccount = () => {},
}) {
  const copy = REASONS[reason] ?? REASONS[SignInBlockedReasonEnum.NoActiveUser];

  const actions = (
    <>
      {/* FigmaButton always renders a <button>, so a button that is really a link is wrapped
          in an anchor — the same idiom ButtonRow uses for its own `href` entries. */}
      <a
        href={copy.actionUrl}
        target='_blank'
        rel='noopener noreferrer'
      >
        <FigmaButton
          label={copy.actionLabel}
          color={ButtonColorsEnum.Primary}
          type={ButtonTypeEnum.NormalIconRight}
          iconName='fa-solid fa-arrow-up-right-from-square'
          iconPosition={IconPositionEnum.Right}
        />
      </a>
      <FigmaButton
        label='Try a different account'
        color={ButtonColorsEnum.Common}
        type={ButtonTypeEnum.NormalIconLeft}
        iconName='fa-solid fa-right-left'
        iconPosition={IconPositionEnum.Left}
        onClick={onTryDifferentAccount}
      />
    </>
  );

  return (
    <StandalonePage>
      <MessageCard
        eyebrow='Bravo access'
        title='Sign-in unavailable'
        iconName='fa-solid fa-lock'
        actions={actions}
      >
        <p className='mcard-lead'>We&rsquo;re unable to sign you in with these credentials.</p>
        <p className='mcard-text'>{copy.body}</p>

        {accountName ? (
          <LabeledValueBox
            label='Account in use'
            value={accountName}
            iconName='fa-solid fa-envelope'
          />
        ) : null}
      </MessageCard>
    </StandalonePage>
  );
}
